import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Trash2, Send } from 'lucide-react';

const formatTime = (t) => {
  if (!isFinite(t) || isNaN(t)) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();
};

export function LiveWaveformCanvas({ stream, active = true }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    if (!stream || !active) return;

    let audioCtx;
    let analyser;
    let source;

    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;
    } catch (e) {
      console.warn('AudioContext not available:', e);
      return;
    }

    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    };

    resize();
    window.addEventListener('resize', resize);

    const data = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      const canvas = canvasRef.current;
      const node = analyserRef.current;
      if (!canvas || !node) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;
      node.getByteFrequencyData(data);

      ctx.clearRect(0, 0, w, h);

      const barWidth = 2.5 * dpr;
      const gap = 4 * dpr;
      const totalBarWidth = barWidth + gap;
      const numberOfBars = Math.floor(w / totalBarWidth);

      for (let i = 0; i < numberOfBars; i++) {
        const sample = data[Math.floor((i / numberOfBars) * data.length)] || 0;
        const amp = Math.max(3 * dpr, (sample / 255) * h * 0.9);
        const x = i * totalBarWidth;
        const y = h / 2 - amp / 2;
        ctx.fillStyle = '#FFCE00'; // Brand gold accent matching entriks-hr
        drawRoundedRect(ctx, x, y, barWidth, amp, 1.5 * dpr);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (source) source.disconnect();
      if (audioCtx && audioCtx.state !== 'closed') audioCtx.close();
      audioCtxRef.current = null;
      analyserRef.current = null;
    };
  }, [stream, active]);

  return <canvas ref={canvasRef} className="h-10 w-full cursor-default" />;
}

// Entriks HR Voice Recording Bar
export function VoiceRecordingBar({ stream, duration = 0, onCancel, onSend }) {
  return (
    <div className="flex w-full items-center gap-3 rounded-2xl border border-zinc-300 bg-zinc-950 p-2.5 px-4 shadow-lg text-white animate-in fade-in duration-200">
      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-600 shadow-md">
        <span className="absolute h-3 w-3 rounded-full bg-white animate-ping opacity-75" />
        <span className="h-3 w-3 rounded-full bg-white" />
      </div>

      <div className="flex-1 flex items-center min-w-0">
        <LiveWaveformCanvas stream={stream} active={true} />
      </div>

      <div className="text-xs font-mono font-bold text-zinc-300 min-w-10 text-right">
        {formatTime(duration)}
      </div>

      <button
        type="button"
        onClick={onCancel}
        title="Anulo regjistrimin"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-zinc-400 hover:bg-zinc-800 hover:text-red-400 transition"
      >
        <Trash2 size={18} />
      </button>

      <button
        type="button"
        onClick={onSend}
        title="Dërgo mesazhin zanor"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFCE00] text-zinc-950 font-bold hover:bg-[#DD0000] hover:text-white transition shadow-md"
      >
        <Send size={16} />
      </button>
    </div>
  );
}

// Player for audio voice note messages
export function AudioPlayer({ audioUrl, duration: initialDuration = 0, isMe = false }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  const cycleRate = () => {
    const next = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : 1;
    setPlaybackRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  return (
    <div
      className={`flex items-center gap-3 rounded-2xl p-2.5 min-w-[240px] max-w-sm ${
        isMe ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-900 border border-zinc-200'
      }`}
    >
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || initialDuration)}
        onEnded={() => setIsPlaying(false)}
      />

      <button
        type="button"
        onClick={togglePlay}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition shadow-sm ${
          isMe
            ? 'bg-[#FFCE00] text-zinc-950 hover:bg-yellow-400'
            : 'bg-zinc-950 text-white hover:bg-zinc-800'
        }`}
      >
        {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
      </button>

      {/* Simulated Waveform Bar */}
      <div className="flex-1 flex flex-col justify-center gap-1 min-w-0">
        <div className="flex items-center gap-0.5 h-6 overflow-hidden">
          {Array.from({ length: 28 }).map((_, i) => {
            const pct = (currentTime / (duration || 1)) * 100;
            const barPct = (i / 28) * 100;
            const isPlayed = barPct <= pct;
            const heights = [8, 14, 22, 10, 18, 24, 12, 16, 20, 10, 22, 14, 8, 18, 22, 14, 10, 20, 16, 12, 24, 18, 10, 14, 22, 12, 16, 8];
            const h = heights[i % heights.length];

            return (
              <span
                key={i}
                className="w-1 rounded-full transition-all"
                style={{
                  height: `${h}px`,
                  backgroundColor: isPlayed
                    ? isMe
                      ? '#FFCE00'
                      : '#18181b'
                    : isMe
                    ? '#52525b'
                    : '#d4d4d8',
                }}
              />
            );
          })}
        </div>

        <div className="flex justify-between text-[10px] text-zinc-400 font-mono">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={cycleRate}
        className={`rounded-lg px-2 py-0.5 text-[11px] font-bold uppercase transition ${
          isMe ? 'bg-zinc-800 text-[#FFCE00]' : 'bg-zinc-200 text-zinc-800'
        }`}
      >
        {playbackRate}x
      </button>
    </div>
  );
}

export default AudioPlayer;
